import { Grid } from '@mui/material';
import { FileDropZone } from 'components/FileDropZone';
import { ACCEPT_ALL } from 'components/FileDropZone/constants/common';
import { TYPE_DROP } from 'components/FileDropZone/FileDropZone';
import { DriverSelect } from 'modules/inventory/provider/carriers/components/DriverSelect';
import { FormSelectProviderType } from 'modules/inventory/provider/common/components/FormSelectProviderType';
import { ProvidersByTypeSelect } from 'modules/inventory/provider/common/components/ProvidersByTypeSelect';
import { PROVIDER_TYPE_ENUM, RESPONSIBLE_TYPE_ENUM } from 'modules/inventory/provider/common/constants';
import { StockReductionCauseSelect } from 'modules/inventory/settings/stock-reduction-cause/components/StockReductionCauseSelect';
import { IStockReductionCause } from 'modules/inventory/settings/stock-reduction-cause/interfaces';
import { memo, useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MAX_SIZE_FILE } from 'settings/globals';
import { FormCompensationField } from '../FormCompensationField';
import { useProductDetail } from 'modules/inventory/product/contexts/ProductDetail';

type Props = {
  control: any;
  setValue: any;
};

const StockReductionCauseForm = ({ control, setValue }: Props) => {
  const { t } = useTranslation('product');
  const { product } = useProductDetail();
  const selectedCause: IStockReductionCause = useWatch({ control, name: 'cause' });
  const selectedProviderType: string = useWatch({ control, name: 'providerType' });
  const responsible: string = useWatch({ control, name: 'responsible' });

  useEffect(() => {
    if (selectedProviderType === 'INTERNAL') {
      setValue('responsible', 'INTERNAL');
      return;
    }
    if (selectedProviderType === RESPONSIBLE_TYPE_ENUM.SUPPLIER) {
      setValue('responsible', product?.providers?.supplier?._id);
      return;
    }
    setValue('responsible', null);
  }, [selectedProviderType, setValue, product?.providers?.supplier?._id]);

  useEffect(() => {
    setValue('driverId', null);
  }, [responsible, setValue]);

  return (
    <>
      <Grid item xs={12}>
        <StockReductionCauseSelect required name='cause' label={t('cause.title')} />
      </Grid>

      {selectedCause?.requiresResponsible && (
        <Grid item container spacing={{ xs: 1, md: 2 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          <Grid item xs={12} md={5}>
            <FormSelectProviderType
              required
              options={Object.values(RESPONSIBLE_TYPE_ENUM)}
              name={'providerType'}
              size='small'
              label={t('common:responsible')}
            />
          </Grid>
          <Grid item xs={12} md={7}>
            <FormCompensationField selectedProviderType={selectedProviderType} control={control} setValue={setValue} />
          </Grid>
          {selectedProviderType !== 'INTERNAL' &&
            <Grid
              item
              xs={12}
              md={selectedProviderType === PROVIDER_TYPE_ENUM.CARRIER ? 6 : 12}
            >
              <ProvidersByTypeSelect
                setValue={setValue}
                required={selectedProviderType !== 'INTERNAL'}
                control={control}
                key={selectedProviderType}
                disabled={!selectedProviderType || selectedProviderType === 'INTERNAL'}
                readOnly={!selectedProviderType || selectedProviderType === 'INTERNAL'}
                name={'responsible'}
                type={selectedProviderType}
                size='small'
                label={t(selectedProviderType ? `common:provider.${selectedProviderType}` : 'common:provider.title')}
                placeholder={t('provider:selectProvider')}
              />
            </Grid>
          }
          {selectedProviderType === PROVIDER_TYPE_ENUM.CARRIER &&
            <Grid item xs={12} md={6}>
              <DriverSelect
                setValue={setValue}
                control={control}
                carrierId={responsible}
                key={selectedProviderType}
                name={'driverId'}
                label={t('common:provider.DRIVER')}
              />
            </Grid>
          }
        </Grid >
      )}

      {
        selectedCause?.requiresEvidence && (
          <Grid item xs={12}>
            <FileDropZone
              required
              name='evidence'
              label={t('common:evidence')}
              dropTitle={t('stock:warehouse.import.fields.uploadFile')}
              type={TYPE_DROP.FILE}
              control={control}
              showDropzoneWrapper
              inputProps={{
                accept: ACCEPT_ALL,
                maxFiles: 4,
                maxSize: MAX_SIZE_FILE,
              }}
            />
          </Grid>
        )
      }
    </>
  );
};

export default memo(StockReductionCauseForm);
